/**
 * Condition-kind model for the FO Rules authoring row.
 *
 * A single condition row now authors THREE kinds of {@link RuleNode}:
 *   - `condition` — numeric metric threshold (the original / default).
 *   - `ranking`   — score-ranking check (mirrors the Flow Tag Rules page).
 *   - `tag`       — flow-tag membership.
 *
 * The picker is one native `<select>` whose option values are namespaced so a
 * tag name can never collide with a metric id or a ranking key:
 *   `metric:<id>` · `rank:<rawScore|scoreShare|optimalWeight>` · `tag:<name>`
 * `decodePickerValue` splits on the FIRST prefix only, so a tag whose name
 * itself contains ':' still round-trips.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type {
  FlowTagRankingMetric,
  FlowTagRankingOp,
  RuleNode,
} from '@trafficpulse/shared';

import type { MetricOption } from '../metric-options';
import { metricOptionGroups } from './metric-groups';

/** The three leaf kinds a single row can author. */
export type ConditionNode = Extract<RuleNode, { kind: 'condition' | 'ranking' | 'tag' }>;

/** A selectable FO ranking tag, with its color + emoji for the chip/label. */
export interface GroupTag {
  name: string;
  color?: string | null;
  emoji?: string | null;
  /** The picker section's name ("Hour tag" in the Automation); default "Flow tag". */
  group?: string;
}

/** Score-ranking metrics with the friendly labels + hover definitions. */
export interface RankingMetricOption {
  key: FlowTagRankingMetric;
  label: string;
  description: string;
}
export const RANKING_METRIC_OPTIONS: RankingMetricOption[] = [
  {
    key: 'rawScore',
    label: 'Raw score',
    description:
      "The optimizer's score for this element — your formula's weighted total. Higher = stronger.",
  },
  {
    key: 'scoreShare',
    label: 'Score share',
    description:
      "This element's share of its pool's total score (its raw score ÷ the pool total). Rules run before the rebalance, so this ranks in the same order as Raw score.",
  },
  {
    key: 'optimalWeight',
    label: 'Optimal weight',
    description:
      'The traffic % the optimizer would give this element after the rebalance. Rules run before the rebalance, so this ranks in the same order as Raw score.',
  },
];

/** Hover definition for a ranking metric (shown on the row + option tooltips). */
export function rankingMetricDescription(metric: FlowTagRankingMetric): string {
  return RANKING_METRIC_OPTIONS.find((o) => o.key === metric)?.description ?? '';
}

/** Ranking operators + whether they need a percent input. */
export interface RankingOpOption {
  op: FlowTagRankingOp;
  label: string;
  needsPercent: boolean;
}
export const RANKING_OP_OPTIONS: RankingOpOption[] = [
  { op: 'highest', label: 'Highest', needsPercent: false },
  { op: 'lowest', label: 'Lowest', needsPercent: false },
  { op: 'top_percent', label: 'Top %', needsPercent: true },
  { op: 'bottom_percent', label: 'Bottom %', needsPercent: true },
];

export function rankingNeedsPercent(op: FlowTagRankingOp): boolean {
  return op === 'top_percent' || op === 'bottom_percent';
}

export function rankingMetricLabel(metric: FlowTagRankingMetric): string {
  return RANKING_METRIC_OPTIONS.find((o) => o.key === metric)?.label ?? metric;
}

// --- Picker value <-> node encoding ---------------------------------------

const RANK_PREFIX = 'rank:';
const TAG_PREFIX = 'tag:';
const METRIC_PREFIX = 'metric:';

/** What the user picked, before any node is built. */
export type PickerSelection =
  | { kind: 'ranking'; metric: FlowTagRankingMetric }
  | { kind: 'tag'; tagName: string }
  | { kind: 'metric'; metric: string };

/** Current picker `value` for a node (what `<select value>` should show). */
export function pickerValue(node: ConditionNode): string {
  if (node.kind === 'ranking') return RANK_PREFIX + node.metric;
  if (node.kind === 'tag') return TAG_PREFIX + node.tagName;
  return METRIC_PREFIX + node.metric;
}

export function decodePickerValue(value: string): PickerSelection {
  if (value.startsWith(RANK_PREFIX)) {
    return { kind: 'ranking', metric: value.slice(RANK_PREFIX.length) as FlowTagRankingMetric };
  }
  if (value.startsWith(TAG_PREFIX)) {
    return { kind: 'tag', tagName: value.slice(TAG_PREFIX.length) };
  }
  const metric = value.startsWith(METRIC_PREFIX) ? value.slice(METRIC_PREFIX.length) : value;
  return { kind: 'metric', metric };
}

// --- Grouped picker options ------------------------------------------------

export interface KindOptionGroup {
  label: string;
  options: { value: string; label: string; title?: string }[];
}

/**
 * Labelled sections for the picker, in order: "Flow tag" first (when the layer
 * has tag rules — they're the quickest pick), then "Score ranking", then the raw
 * "Metrics". Ranking options carry their definition as a hover `title`.
 */
export function buildKindOptionGroups(
  metrics: MetricOption[],
  tags: GroupTag[],
  /** A tag the row already names (a preset's BEST) that the flow has no tag
   *  rule for yet — listed so the select still shows the selection. */
  currentTag?: string,
  /** Score-ranking fields offered (default all; [] = none — the Automation's Exclude rules). */
  rankingMetrics?: ReadonlyArray<FlowTagRankingMetric>,
): KindOptionGroup[] {
  const groups: KindOptionGroup[] = [];
  if (currentTag && !tags.some((t) => t.name === currentTag)) {
    tags = [...tags, { name: `${currentTag}` }];
  }
  if (tags.length > 0) {
    groups.push({
      label: tags.find((t) => t.group)?.group ?? 'Flow tag',
      // The emoji rides the option label so it shows in the native dropdown; the
      // selected tag also paints the <select> in its color (RuleConditionRow).
      options: tags.map((t) => ({
        value: TAG_PREFIX + t.name,
        label: t.emoji ? `${t.emoji} ${t.name}` : t.name,
      })),
    });
  }
  const ranking = RANKING_METRIC_OPTIONS.filter((o) => !rankingMetrics || rankingMetrics.includes(o.key));
  if (ranking.length > 0)
    groups.push({
      label: 'Score ranking',
      options: ranking.map((o) => ({
        value: RANK_PREFIX + o.key,
        label: o.label,
        title: o.description,
      })),
    });
  groups.push(...metricOptionGroups(metrics, METRIC_PREFIX));
  return groups;
}
